/**
 * A mission, as data.
 *
 * `MissionSpec` is the whole of what a generator produces and the whole of what
 * the sim, the briefing screen and the debrief read. It is plain JSON — no
 * functions, no live state — so it survives a URL, a save file and a test
 * fixture unchanged, and two runs of the same spec are the same mission.
 *
 * The split from `EncounterSetup` is deliberate. An encounter knows about
 * aeroplanes and rounds; a mission knows about *intent* — who these people are,
 * where they are going, and what would count as having done the job.
 * `encounterSetupFrom` is the one-way door between them.
 */

import type { AircraftKey } from "../flight/aircraft-table.ts";
import { attitudeFromAngles, rotateVec3, scaleVec3, BODY_FORWARD, type Vec3 } from "../flight/math3d.ts";
import { buildAircraftSpec } from "../flight/aircraft-spec.ts";
import { FLAT_GROUND, type GroundQuery } from "../flight/ground.ts";
import type {
  BombLoadSpec,
  EncounterAircraftSpec,
  EncounterPilot,
  EncounterSetup,
  EncounterSide,
} from "../combat/encounter.ts";
import type { RouteOrders, Waypoint } from "../combat/route.ts";
import { DEFAULT_AMMUNITION, type AmmunitionType, type EncounterTargetSpec, type GroundRoute, type TargetKind } from "../combat/targets.ts";
import { searchlightPosts } from "../combat/searchlight-posts.ts";
import { waterOf, type ShipSpec } from "../combat/ship-deployment.ts";
import { deployMissionShips } from "./ship-ensign.ts";
import type { CombatLevel, FlakClass } from "../combat/flak.ts";
import type { FlightModelTier, RealismSettings } from "../flight/tiers.ts";
import type { MissionObjective } from "./objectives.ts";
import type { RaidPlan } from "./raid.ts";
import type { MissionType } from "./mission-types.ts";
import type { Nation } from "./mission-roster.ts";
import type {
  FrontLine,
  MissionEra,
  MissionRegion,
  MissionWeather,
} from "./mission-world.ts";

/** One aeroplane in the mission, player or AI. */
export interface MissionFlight {
  readonly id: string;
  readonly side: EncounterSide;
  readonly nation: Nation;
  readonly aircraft: AircraftKey;
  readonly pilot: EncounterPilot;
  /** What the briefing and the HUD call this aeroplane, e.g. `"Werner Voss"` or `"Recon 1"`. */
  readonly callsign: string;
  /**
   * Spawn position in world metres. `y` is the starting altitude — or, for a
   * flight that starts `onGround`, the *terrain* height at the spawn, since the
   * aeroplane is then placed on top of its own undercarriage.
   */
  readonly position: Vec3;
  readonly headingRad: number;
  /** `PS.EXE`'s START AT YOUR AERODROME: this flight begins with its wheels on the ground. */
  readonly onGround?: boolean;
  /** Left to `createSim`'s own 0.8 × Vmax default when omitted. */
  readonly airspeedMps?: number;
  /** Where this flight is going when it is not fighting (`combat/route.ts`). */
  readonly waypoints?: readonly Waypoint[];
  /** Whether this flight may abandon that route to fight. Defaults to `engage`. */
  readonly routeOrders?: RouteOrders;
  readonly ammunition?: AmmunitionType;
  /** What it is carrying, and what it is carrying it to (`combat/bombs.ts`). Bombers only. */
  readonly bombs?: BombLoadSpec;
  /** Static targets this flight was sent to attack, rather than the nearest aeroplane. */
  readonly strikeTargetIds?: readonly string[];
}

export interface MissionTarget {
  readonly id: string;
  readonly kind: TargetKind;
  readonly side: EncounterSide;
  readonly position: Vec3;
  readonly headingRad: number;
  readonly label: string;
  /** A Zeppelin on its way in carries a rack; nothing else does (`combat/targets.ts`). */
  readonly bombs?: BombLoadSpec;
  /**
   * The two-point line a train runs, and how far along it it already is. Only
   * the `train` kind carries these; see `combat/ground-route.ts`.
   */
  readonly route?: GroundRoute;
  readonly routeTravelledM?: number;
}

/**
 * The player's home aerodrome, when a mission has one.
 *
 * The *name* is a real `AERO.DAT` field (`campaign/aerodromes.ts`); the world
 * position is not, and cannot be — docs/formats/world-terrain.md §2.4 leaves
 * the `AERO.DAT` map pixel ↔ `.WLD` world transform explicitly unresolved. So
 * the mission places its own field in the port's own front-line geometry
 * (`mission-world.ts`) and carries the real name along with it, exactly as the
 * app's map projection does.
 *
 * `headingRad` is the take-off/landing direction. There is no runway heading in
 * any decoded file either, so a generator picks one and says so.
 */
export interface MissionBase {
  readonly name: string;
  /** World metres; `y` is the terrain height at the field. */
  readonly position: Vec3;
  readonly headingRad: number;
  /** How close to the field counts as being at it, for landing. */
  readonly radiusM: number;
}

/**
 * An anti-aircraft emplacement. Separate from `MissionTarget` because it is the
 * opposite kind of thing: a battery shoots and cannot be shot, has no health and
 * no model, and never appears in a debrief column (`combat/flak.ts`).
 */
export interface MissionFlak {
  readonly id: string;
  readonly side: EncounterSide;
  /** Ground position; `y` is the emplacement's altitude, 0 for level ground. */
  readonly position: Vec3;
  readonly batteryClass: FlakClass;
}

export interface MissionSpec {
  /** Stable id: the catalog entry plus the seed, so a URL round-trips a mission. */
  readonly id: string;
  readonly type: MissionType;
  readonly title: string;
  readonly seed: number;
  readonly era: MissionEra;
  readonly region: MissionRegion;
  readonly frontLine: FrontLine;
  readonly weather: MissionWeather;
  readonly playerNation: Nation;
  readonly playerFlightId: string;
  readonly tier: FlightModelTier;
  readonly realism: Partial<RealismSettings>;
  /**
   * The Realism Panel's COMBAT LEVEL (AD-010 §9.4, player-only flak scaling).
   * Every generator stamps `MISSION_COMBAT_LEVEL` (`mission-options.ts`) by
   * default — `AVERAGE`, matching the panel's port default — and
   * `applyRealism` overwrites it once a player actually opens the panel.
   * Optional so an `EncounterSetup` built by hand (a duel, a bare fixture) can
   * still omit it: `combat/flak.ts`'s `combatLevelScale` then resolves
   * `undefined` to full damage, this port's behaviour before the panel had
   * the control.
   */
  readonly combatLevel?: CombatLevel;
  /**
   * **ENHANCED SIMULATION** — the port's own preference, and the only one that
   * changes the simulation rather than the picture
   * (`apps/web/src/flight/sim-settings.ts`).
   *
   * It carries exactly one thing today: whether ships are put to sea
   * (`shipsFor`). Optional, and `undefined` on every mission every generator in
   * this port produces, which is what makes the whole feature bit-zero — a
   * spec that does not name it deploys nothing, draws nothing and hashes to
   * exactly the string it did before the field existed.
   *
   * It rides the spec rather than a runner argument for the reason
   * `combatLevel` does: a sortie is reproducible from its spec, and a replay of
   * a mission flown with ships in it has to put the same ships back.
   */
  readonly enhancedSimulation?: boolean;
  readonly flights: readonly MissionFlight[];
  readonly targets: readonly MissionTarget[];
  /** Anti-aircraft batteries. Absent on mission types the original leaves undefended. */
  readonly flak?: readonly MissionFlak[];
  readonly objectives: readonly MissionObjective[];
  /** The player's aerodrome, when the mission has one to take off from or land back at. */
  readonly homeBase?: MissionBase;
  /**
   * Present on the four raid-family missions, absent on everything else. It is
   * what lets the debriefing say how the *raid* went rather than only how the
   * player's own sortie went (`raid.ts`).
   */
  readonly raid?: RaidPlan;
  /** The briefing paragraph, already assembled from `PS.EXE`'s template shape. */
  readonly briefing: readonly string[];
  /**
   * What this port does *not* model for this mission type, in plain words, kept
   * out of the in-fiction briefing and shown separately. A player who is told
   * "balloon busting" and finds no flak battery shooting back deserves to know
   * that is a gap rather than a design choice.
   */
  readonly portNotes: readonly string[];
}

/**
 * Applies to every generated mission. The list has been shrinking. Wind came
 * off it when AD-007k recovered the original's wind model out of `RB.EXE` and
 * wired it — the mission's wind word drives a real altitude-banded drift, gated
 * on REALISTIC WEATHER. Time of day came off it when the sky got its sun, its
 * astronomy and its lighting (`flight/time-of-day.ts`, `flight/sky-*.ts`). What
 * is left is G42: the cloud field is drawn and flown through, and
 * `packages/engine` never reads it, so nothing is ever hidden inside one.
 */
export const WEATHER_IS_COSMETIC =
  "Cloud is drawn and flown through but conceals nothing: neither you nor the enemy can break contact in it, so the manual's cloud tactics do not work here. Wind and the light of the hour are simulated — wind drifts the aircraft with altitude when REALISTIC WEATHER is on.";

export function playerFlightOf(spec: MissionSpec): MissionFlight {
  const flight = spec.flights.find((entry) => entry.id === spec.playerFlightId);
  if (!flight) throw new Error(`mission ${spec.id} has no flight ${spec.playerFlightId}`);
  return flight;
}

export function flightById(spec: MissionSpec, id: string): MissionFlight | undefined {
  return spec.flights.find((entry) => entry.id === id);
}

/**
 * A cruise speed for a flight that did not name one: fast enough to be flying
 * properly, slow enough that a formation is not scattered by tick one.
 */
const CRUISE_FRACTION = 0.82;

/**
 * The terrain height a ground-started flight is actually seated on.
 *
 * `MissionFlight.position.y` is documented as *"the terrain height at the
 * spawn"* for an `onGround` flight, and every generator writes **0** there —
 * because a generator is pure and cannot know what the region's ground is
 * doing under the field it picked. Nobody resolved it afterwards either, so a
 * ground start was always placed on the flat datum whatever the `GroundQuery`
 * said underneath.
 *
 * That is harmless where the terrain is above the datum (the aeroplane spawns
 * inside the hill and `ground-contact.ts` snaps it up on tick one) and it is a
 * **crash** where the terrain is below it. Measured on the shipped regions at
 * Scramble's own spawn, world (0, 4200):
 *
 * | region | terrain at the spawn |
 * |---|---:|
 * | London, Dunkirk | 0.00 m |
 * | Verdun | +2.11 m |
 * | Paris | +7.12 m |
 * | **Somme** | **−22.35 m** |
 *
 * On the Somme the Camel was spawned 23 m in the air with its wheels down, fell
 * for 2.2 seconds while the throttle wound on, was flagged `YOUR PLANE IS
 * STALLED!` on the way down (below the 1-g stall speed and no longer on the
 * wheels) and arrived at 36 mph with the undercarriage folded and the sortie
 * over — from the player's seat, a take-off that crashes on the ground roll.
 *
 * So the height is resolved here, at the one place that has both the spec and
 * the ground: the generator states *where*, the run states *what is under it*.
 */
function seatOnGround(flight: MissionFlight, ground: GroundQuery): MissionFlight {
  if (!flight.onGround) return flight;
  const heightM = ground.groundHeightAt(flight.position.x, flight.position.z);
  if (!Number.isFinite(heightM) || heightM === flight.position.y) return flight;
  return { ...flight, position: { ...flight.position, y: heightM } };
}

function aircraftSpecFor(flight: MissionFlight, spec: MissionSpec): EncounterAircraftSpec {
  // A flight on its wheels is not cruising: it is stationary at its aerodrome
  // with the throttle closed until somebody opens it.
  const airspeedMps = flight.onGround
    ? (flight.airspeedMps ?? 0)
    : (flight.airspeedMps ?? buildAircraftSpec(flight.aircraft).vmaxMps * CRUISE_FRACTION);
  return {
    id: flight.id,
    side: flight.side,
    pilot: flight.pilot,
    // `createSim` always starts an aeroplane at the world origin, so the whole
    // spawn position rides in `offsetM` — including its `y`, which cancels
    // against `altitudeM` below to leave the aeroplane exactly where the
    // mission put it.
    offsetM: { x: flight.position.x, y: 0, z: flight.position.z },
    waypoints: flight.waypoints,
    routeOrders: flight.routeOrders,
    ammunition: flight.ammunition,
    bombs: flight.bombs,
    strikeTargetIds: flight.strikeTargetIds,
    flight: {
      aircraft: flight.aircraft,
      tier: spec.tier,
      realism: spec.realism,
      // AD-007k: the mission's own wind word is the original's scenario weather
      // byte, and every aeroplane in the encounter flies in the same air — which
      // is also what keeps a lockstep peer's wind identical without exchanging
      // anything, since the whole encounter is rebuilt from this one spec.
      weather: spec.weather.wind,
      // The same one `ammunition` above, so the sim's jam threshold and the
      // encounter's balloon-damage model can never be flying different belts.
      ammunition: flight.ammunition ?? DEFAULT_AMMUNITION,
      altitudeM: flight.position.y,
      headingRad: flight.headingRad,
      airspeedMps,
      onGround: flight.onGround,
      trimmed: !flight.onGround,
    },
  };
}

/**
 * The searchlights a sortie is flown under, or none.
 *
 * They are derived here rather than authored into `MissionSpec.targets`, and
 * that placement is the point. A `MissionTarget` is *intent* — something a
 * generator decided this sortie should be about, something the briefing map
 * marks and the debrief counts. A searchlight is neither: it is a fitting that
 * comes with a battery, the way a battery's five guns come with it, and it
 * exists in the encounter for one reason only — so it can be shot out and the
 * guns behind it made to fire blind (AD-017 §11). Keeping it on this side of
 * the one-way door leaves the mission census, the map and the raid tally
 * untouched, and leaves `spec.targets` still meaning what it says.
 *
 * Night only, tested as `timeOfDay === "night"`. The renderer asks the same
 * question the other way round — `sunDirectionAt(...) === undefined`, which is
 * `sun.c` `0xf9f5`'s own day/night switch — and of the four buckets only
 * `night` puts the sun under the horizon, so the two agree today. They are
 * written differently because they *are* different questions: the sky asks
 * whether a beam would be visible, and this asks whether a crew turned their
 * lights on.
 */
function searchlightsFor(spec: MissionSpec): EncounterTargetSpec[] {
  return spec.weather.timeOfDay === "night" ? searchlightPosts(spec.flak ?? []) : [];
}

/**
 * The ships at sea for this sortie, or none — ENHANCED SIMULATION's entire
 * entry point into the simulation.
 *
 * Derived here rather than authored into `MissionSpec.targets`, on exactly the
 * one-way-door argument `searchlightsFor` makes above: a `MissionTarget` is
 * *intent*, something a generator decided the sortie should be about and the
 * briefing map marks. An escort steaming her patrol is not intent. She is
 * weather.
 *
 * ## Three gates, in order, and each of them alone is enough to give none
 *
 * 1. **The preference.** `spec.enhancedSimulation` is `undefined` on every
 *    mission every generator in this port produces.
 * 2. **The sea.** `waterOf(ground)` is dry unless the caller injected a region's
 *    water mask through `withWater`, so every fixture, every census and
 *    `engine-fingerprint.ts`'s own probe — all of which step on `FLAT_GROUND` —
 *    find no water anywhere.
 * 3. **The margin.** A region whose water is rivers and ponds cannot hold a
 *    patrol with `SHIP_WATER_MARGIN_M` round every metre of it, so it gets
 *    none without any list of regions being written down.
 *
 * ## Who owns the water
 *
 * `ship-ensign.ts`, which is where the whole of that argument now lives: whether
 * a region's water carries one navy or two follows `REGION_HAS_FRONT_LINE` — a
 * per-region fact recovered from the game's own `MAP1.DAT` — and never
 * `DEFAULT_FRONT_LINE`'s placeholder coordinate, which is the trap that put 434
 * flak belts in the North Sea and the reason `ENSIGN_CENTRAL` used to fly
 * nowhere.
 */
function shipsFor(spec: MissionSpec, ground: GroundQuery): readonly ShipSpec[] {
  if (spec.enhancedSimulation !== true) return [];
  return deployMissionShips({
    id: spec.id,
    seed: spec.seed,
    region: spec.region,
    playerNation: spec.playerNation,
    isWater: waterOf(ground),
  });
}

/**
 * The runnable encounter behind a mission. Pure, and stable for a given spec
 * **and ground** — `ground` is the same `GroundQuery` the sim will be stepped
 * with, and it is read to seat the flights that start on their wheels
 * (`seatOnGround`) and to find the sea a ship may patrol (`shipsFor`).
 * Defaulting it to `FLAT_GROUND` keeps every existing caller on the flat datum
 * it already assumed — and, since a flat datum is dry, with no ships.
 */
export function encounterSetupFrom(spec: MissionSpec, ground: GroundQuery = FLAT_GROUND): EncounterSetup {
  const ships = shipsFor(spec, ground);
  return {
    seed: spec.seed,
    ...(ships.length > 0 ? { ships } : {}),
    aircraft: spec.flights.map((flight) => aircraftSpecFor(seatOnGround(flight, ground), spec)),
    targets: [
      ...spec.targets.map((target) => ({
        id: target.id,
        kind: target.kind,
        side: target.side,
        position: target.position,
        headingRad: target.headingRad,
        label: target.label,
        bombs: target.bombs,
        route: target.route,
        routeTravelledM: target.routeTravelledM,
      })),
      ...searchlightsFor(spec),
    ],
    flak: spec.flak,
    combatLevel: spec.combatLevel,
  };
}

/** A point `distanceM` along `headingRad` from `origin`, at `altitudeM`. */
export function pointAlong(origin: Vec3, headingRad: number, distanceM: number, altitudeM: number): Vec3 {
  const forward = rotateVec3(attitudeFromAngles(headingRad, 0, 0), BODY_FORWARD);
  const offset = scaleVec3(forward, distanceM);
  return { x: origin.x + offset.x, y: altitudeM, z: origin.z + offset.z };
}
